import React, { useCallback, useEffect, useState } from 'react';
import { NavLink } from 'react-router-dom';
import sharedStyles from './AdminShared.module.scss';
import styles from './AdminPaymentOperations.module.scss';
import {
    PaymentOperationsSummaryDto,
    PaymentRecoveryResultDto
} from '../../../types/admin.types';
import {
    fetchPaymentOperations,
    reconcilePaymentSubscription,
    reconcileVideoPurchase,
    retryStripeWebhook
} from '../../../services/admin.service';
import { useUserState } from '../../../providers/user-state.provider.ts';
import { Routes } from '../../../types/constants';

const emptySummary: PaymentOperationsSummaryDto = {
    EvaluatedAtUtc: '',
    AttentionCount: 0,
    ActiveDisputeCount: 0,
    Thresholds: {
        RecentFailureWindowHours: 24,
        WebhookProcessingStaleMinutes: 10,
        IncompleteSubscriptionHours: 24,
        PendingVideoPurchaseMinutes: 1500,
        ReconciliationStaleHours: 36,
        MaxItems: 100
    },
    WebhookIssues: [],
    UnmatchedObjects: [],
    IncompleteSubscriptions: [],
    PendingVideoPurchases: [],
    FailedPaymentsByCategory: [],
    RefundFailures: [],
    ReconciliationDifferences: [],
    SubscriptionSyncIssues: []
};

export const formatDateTime = (value?: string | null) => {
    if (!value) {
        return '-';
    }

    const hasTimeZone = /(?:z|[+-]\d{2}:?\d{2})$/i.test(value);
    const parsed = new Date(hasTimeZone ? value : `${value}Z`);
    return Number.isNaN(parsed.getTime()) ? value : parsed.toLocaleString();
};

const formatToken = (value?: string | null) => value?.replace(/_/g, ' ') ?? '-';

const formatMinutes = (value: number) => value >= 60 && value % 60 === 0
    ? `${value / 60} hours`
    : `${value} minutes`;

const AdminPaymentOperations = () => {
    const { getUserInfo } = useUserState();
    const token = getUserInfo()?.token;
    const [summary, setSummary] = useState(emptySummary);
    const [isLoading, setIsLoading] = useState(true);
    const [activeAction, setActiveAction] = useState<string | null>(null);
    const [armedAction, setArmedAction] = useState<string | null>(null);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [recoveryResult, setRecoveryResult] = useState<PaymentRecoveryResultDto | null>(null);

    const loadSummary = useCallback(async () => {
        setIsLoading(true);
        setArmedAction(null);
        setErrorMessage(null);
        try {
            setSummary(await fetchPaymentOperations(token));
        } catch {
            setErrorMessage('Unable to load payment operations.');
        } finally {
            setIsLoading(false);
        }
    }, [token]);

    useEffect(() => {
        loadSummary();
    }, [loadSummary]);

    const runRecovery = async (
        actionKey: string,
        action: () => Promise<PaymentRecoveryResultDto>
    ) => {
        setArmedAction(null);
        setActiveAction(actionKey);
        setErrorMessage(null);
        setRecoveryResult(null);
        try {
            setRecoveryResult(await action());
            await loadSummary();
        } catch {
            setErrorMessage('The recovery action could not be completed. Check the latest safe status and try again.');
        } finally {
            setActiveAction(null);
        }
    };

    const actionButton = (
        label: string,
        actionKey: string,
        action: () => Promise<PaymentRecoveryResultDto>
    ) => {
        const isArmed = armedAction === actionKey;
        return (
            <button
                type="button"
                className={`${sharedStyles.secondary} ${isArmed ? styles.confirmAction : ''}`}
                disabled={activeAction !== null}
                onBlur={() => setArmedAction(current => current === actionKey ? null : current)}
                onClick={() => isArmed ? runRecovery(actionKey, action) : setArmedAction(actionKey)}
            >
                {activeAction === actionKey ? 'Working...' : isArmed ? `Confirm ${label}` : label}
            </button>
        );
    };

    if (isLoading && !summary.EvaluatedAtUtc) {
        return <div className={sharedStyles.muted}>Loading payment operations...</div>;
    }

    const overdueSyncIssues = summary.SubscriptionSyncIssues.filter(issue => issue.IssueType === 'reconciliation_overdue');
    const failedSyncIssues = summary.SubscriptionSyncIssues.filter(issue => issue.IssueType !== 'reconciliation_overdue');
    const overdueCountLabel = overdueSyncIssues.length >= summary.Thresholds.MaxItems
        ? `At least ${overdueSyncIssues.length}`
        : overdueSyncIssues.length.toString();

    return (
        <div className={sharedStyles.page}>
            <header className={sharedStyles.header}>
                <div>
                    <h1>Payment Operations</h1>
                    <p>Review safe payment classifications and run bounded reconciliation actions.</p>
                </div>
                <button type="button" className={sharedStyles.secondary} onClick={loadSummary} disabled={isLoading || activeAction !== null}>
                    {isLoading ? 'Refreshing...' : 'Refresh'}
                </button>
            </header>

            {errorMessage ? <div className={styles.error} role="alert">{errorMessage}</div> : null}
            {recoveryResult ? (
                <div
                    className={recoveryResult.Outcome === 'recovered' ? styles.success : styles.warning}
                    role="status"
                >
                    Recovery {formatToken(recoveryResult.Outcome)} for {recoveryResult.ResourceId}
                    {recoveryResult.CurrentStatus ? ` (${formatToken(recoveryResult.CurrentStatus)})` : ''}.
                </div>
            ) : null}

            <section className={styles.summaryGrid} aria-label="Payment operations summary">
                <article className={styles.summaryCard}>
                    <strong>{summary.AttentionCount}</strong>
                    <span>Items requiring review</span>
                </article>
                <article className={styles.summaryCard}>
                    <strong>{summary.WebhookIssues.length}</strong>
                    <span>Webhook issues</span>
                </article>
                <article className={styles.summaryCard}>
                    <strong>{summary.UnmatchedObjects.length}</strong>
                    <span>Unmatched objects</span>
                </article>
                <article className={styles.summaryCard}>
                    <strong>{summary.IncompleteSubscriptions.length}</strong>
                    <span>Old incomplete subscriptions</span>
                </article>
                <article className={styles.summaryCard}>
                    <strong>{summary.PendingVideoPurchases.length}</strong>
                    <span>Old pending purchases</span>
                </article>
                <article className={styles.summaryCard}>
                    <strong>{summary.ActiveDisputeCount}</strong>
                    <span>Open disputes</span>
                    <NavLink to={Routes.ADMIN_PAYMENT_DISPUTES}>Review disputes</NavLink>
                </article>
            </section>

            <div className={styles.thresholds}>
                Evaluated {formatDateTime(summary.EvaluatedAtUtc)}. Stale after: webhook {summary.Thresholds.WebhookProcessingStaleMinutes} minutes,
                incomplete subscription {summary.Thresholds.IncompleteSubscriptionHours} hours, pending purchase {formatMinutes(summary.Thresholds.PendingVideoPurchaseMinutes)},
                reconciliation {summary.Thresholds.ReconciliationStaleHours} hours. Each list is limited to {summary.Thresholds.MaxItems} items.
            </div>

            <section className={`${sharedStyles.panel} ${sharedStyles.fullWidthPanel}`}>
                <h2>Webhook delivery and processing</h2>
                {summary.WebhookIssues.length === 0 ? (
                    <div className={sharedStyles.empty}>No failed or stale webhook receipts.</div>
                ) : (
                    <div className={styles.tableScroll}>
                        <table className={sharedStyles.table}>
                            <thead><tr><th>Event</th><th>Type</th><th>Status</th><th>Category</th><th>Received</th><th>Action</th></tr></thead>
                            <tbody>
                                {summary.WebhookIssues.map((issue) => (
                                    <tr key={issue.StripeEventId}>
                                        <td className={styles.identifier}>{issue.StripeEventId}</td>
                                        <td>{issue.EventType}</td>
                                        <td>{formatToken(issue.Status)}</td>
                                        <td>{formatToken(issue.FailureCategory)}</td>
                                        <td>{formatDateTime(issue.ReceivedAtUtc)}</td>
                                        <td>{issue.CanRetry ? actionButton('Retry', `webhook:${issue.StripeEventId}`, () => retryStripeWebhook(issue.StripeEventId, token)) : '-'}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </section>

            <section className={`${sharedStyles.panel} ${sharedStyles.fullWidthPanel}`}>
                <h2>Unmatched Stripe objects</h2>
                {summary.UnmatchedObjects.length === 0 ? (
                    <div className={sharedStyles.empty}>No unmatched customers, subscriptions, or Checkout Sessions.</div>
                ) : (
                    <div className={styles.tableScroll}>
                        <table className={sharedStyles.table}>
                            <thead><tr><th>Kind</th><th>Stripe object</th><th>Customer</th><th>Event</th><th>Category</th><th>Observed</th><th>Action</th></tr></thead>
                            <tbody>
                                {summary.UnmatchedObjects.map((issue, index) => {
                                    const actionKey = issue.StripeEventId ?? issue.StripeObjectId ?? String(index);
                                    const canReconcileSubscription = issue.Kind === 'subscription' && issue.StripeObjectId?.startsWith('sub_');
                                    return (
                                        <tr key={`${issue.Kind}:${actionKey}`}>
                                            <td>{formatToken(issue.Kind)}</td>
                                            <td className={styles.identifier}>{issue.StripeObjectId ?? '-'}</td>
                                            <td className={styles.identifier}>{issue.StripeCustomerId ?? '-'}</td>
                                            <td className={styles.identifier}>{issue.StripeEventId ?? '-'}</td>
                                            <td>{formatToken(issue.FailureCategory)}</td>
                                            <td>{formatDateTime(issue.ObservedAtUtc)}</td>
                                            <td>
                                                {issue.CanRetry && issue.StripeEventId
                                                    ? actionButton('Retry event', `unmatched:${actionKey}`, () => retryStripeWebhook(issue.StripeEventId!, token))
                                                    : canReconcileSubscription
                                                        ? actionButton('Reconcile', `unmatched:${actionKey}`, () => reconcilePaymentSubscription(issue.StripeObjectId!, token))
                                                        : '-'}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </section>

            <section className={`${sharedStyles.panel} ${sharedStyles.fullWidthPanel}`}>
                <h2>Incomplete subscriptions</h2>
                {summary.IncompleteSubscriptions.length === 0 ? (
                    <div className={sharedStyles.empty}>No incomplete subscriptions exceed the configured age.</div>
                ) : (
                    <div className={styles.tableScroll}>
                        <table className={sharedStyles.table}>
                            <thead><tr><th>Subscription</th><th>Customer</th><th>Status</th><th>Created</th><th>Action</th></tr></thead>
                            <tbody>
                                {summary.IncompleteSubscriptions.map((issue) => (
                                    <tr key={issue.SubscriptionId}>
                                        <td className={styles.identifier}>{issue.StripeSubscriptionId}</td>
                                        <td className={styles.identifier}>{issue.StripeCustomerId}</td>
                                        <td>{formatToken(issue.Status)}</td>
                                        <td>{formatDateTime(issue.CreatedAtUtc)}</td>
                                        <td>{actionButton('Reconcile', `subscription:${issue.StripeSubscriptionId}`, () => reconcilePaymentSubscription(issue.StripeSubscriptionId, token))}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </section>

            <section className={`${sharedStyles.panel} ${sharedStyles.fullWidthPanel}`}>
                <h2>Pending video purchases</h2>
                {summary.PendingVideoPurchases.length === 0 ? (
                    <div className={sharedStyles.empty}>No pending video purchases exceed the configured age.</div>
                ) : (
                    <div className={styles.tableScroll}>
                        <table className={sharedStyles.table}>
                            <thead><tr><th>Purchase</th><th>Checkout Session</th><th>Video</th><th>Created</th><th>Action</th></tr></thead>
                            <tbody>
                                {summary.PendingVideoPurchases.map((issue) => (
                                    <tr key={issue.PurchaseId}>
                                        <td className={styles.identifier}>{issue.PurchaseId}</td>
                                        <td className={styles.identifier}>{issue.StripeCheckoutSessionId ?? '-'}</td>
                                        <td className={styles.identifier}>{issue.VideoId}</td>
                                        <td>{formatDateTime(issue.CreatedAtUtc)}</td>
                                        <td>{actionButton('Reconcile', `purchase:${issue.PurchaseId}`, () => reconcileVideoPurchase(issue.PurchaseId, token))}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </section>

            <section className={`${sharedStyles.panel} ${sharedStyles.fullWidthPanel}`}>
                <h2>Failed payments by safe category</h2>
                {summary.FailedPaymentsByCategory.length === 0 ? (
                    <div className={sharedStyles.empty}>No failed payments in the recent window.</div>
                ) : (
                    <div className={styles.tableScroll}>
                        <table className={sharedStyles.table}>
                            <thead><tr><th>Category</th><th>Count</th><th>Last observed</th></tr></thead>
                            <tbody>
                                {summary.FailedPaymentsByCategory.map((item) => (
                                    <tr key={item.FailureCategory}>
                                        <td>{formatToken(item.FailureCategory)}</td>
                                        <td>{item.Count}</td>
                                        <td>{formatDateTime(item.LastObservedAtUtc)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </section>

            <section className={`${sharedStyles.panel} ${sharedStyles.fullWidthPanel}`}>
                <h2>Refund failures</h2>
                {summary.RefundFailures.length === 0 ? (
                    <div className={sharedStyles.empty}>No refund failures in the recent window.</div>
                ) : (
                    <div className={styles.tableScroll}>
                        <table className={sharedStyles.table}>
                            <thead><tr><th>Refund</th><th>Event</th><th>Status</th><th>Category</th><th>Observed</th><th>Action</th></tr></thead>
                            <tbody>
                                {summary.RefundFailures.map((issue) => (
                                    <tr key={issue.StripeEventId}>
                                        <td className={styles.identifier}>{issue.StripeRefundId ?? '-'}</td>
                                        <td className={styles.identifier}>{issue.StripeEventId}</td>
                                        <td>{formatToken(issue.Status)}</td>
                                        <td>{formatToken(issue.FailureCategory)}</td>
                                        <td>{formatDateTime(issue.ObservedAtUtc)}</td>
                                        <td>{issue.CanRetry ? actionButton('Retry', `refund:${issue.StripeEventId}`, () => retryStripeWebhook(issue.StripeEventId, token)) : '-'}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </section>

            <section className={`${sharedStyles.panel} ${sharedStyles.fullWidthPanel}`}>
                <h2>Subscription reconciliation</h2>
                {summary.SubscriptionSyncIssues.length === 0 && summary.ReconciliationDifferences.length === 0 ? (
                    <div className={sharedStyles.empty}>No overdue, failed, or differing subscription reconciliations.</div>
                ) : null}
                {overdueSyncIssues.length > 0 ? (
                    <div className={styles.systemNotice} role="status">
                        <strong>Scheduled subscription verification is overdue</strong>
                        <p>
                            {overdueCountLabel} active subscriptions are waiting for the automated sync job.
                            This is a system-level warning; individual reconciliation is not required.
                        </p>
                        <details>
                            <summary>View affected subscriptions</summary>
                            <div className={styles.tableScroll}>
                                <table className={sharedStyles.table}>
                                    <thead><tr><th>Subscription</th><th>State</th><th>Last verified</th></tr></thead>
                                    <tbody>
                                        {overdueSyncIssues.map((issue) => (
                                            <tr key={`overdue:${issue.SubscriptionId}`}>
                                                <td className={styles.identifier}>{issue.StripeSubscriptionId}</td>
                                                <td>{formatToken(issue.Status)}</td>
                                                <td>{formatDateTime(issue.LastStripeSyncUtc ?? issue.LastStripeSuccessUtc)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </details>
                    </div>
                ) : null}
                {failedSyncIssues.length > 0 || summary.ReconciliationDifferences.length > 0 ? (
                    <div className={styles.tableScroll}>
                        <table className={sharedStyles.table}>
                            <thead><tr><th>Subscription</th><th>Issue</th><th>State</th><th>Observed</th><th>Action</th></tr></thead>
                            <tbody>
                                {failedSyncIssues.map((issue) => (
                                    <tr key={`sync:${issue.SubscriptionId}`}>
                                        <td className={styles.identifier}>{issue.StripeSubscriptionId}</td>
                                        <td>{formatToken(issue.IssueType)}{issue.FailureCategory ? ` / ${formatToken(issue.FailureCategory)}` : ''}</td>
                                        <td>{formatToken(issue.Status)}</td>
                                        <td>{formatDateTime(issue.LastStripeSyncUtc ?? issue.LastStripeSuccessUtc)}</td>
                                        <td>{actionButton('Reconcile', `sync:${issue.StripeSubscriptionId}`, () => reconcilePaymentSubscription(issue.StripeSubscriptionId, token))}</td>
                                    </tr>
                                ))}
                                {summary.ReconciliationDifferences.map((issue) => (
                                    <tr key={`difference:${issue.AuditEventId}`}>
                                        <td className={styles.identifier}>{issue.StripeSubscriptionId}</td>
                                        <td>reconciliation difference</td>
                                        <td>{formatToken(issue.PreviousStatus)} to {formatToken(issue.NewStatus)}</td>
                                        <td>{formatDateTime(issue.ObservedAtUtc)}</td>
                                        <td>{actionButton('Reconcile', `difference:${issue.AuditEventId}`, () => reconcilePaymentSubscription(issue.StripeSubscriptionId, token))}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                ) : null}
            </section>
        </div>
    );
};

export default AdminPaymentOperations;
